/* global monaco */

import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import GridContent from 'zignal-landscape/lib/GridContent';
import themeable from 'zignal-landscape/lib/themeable';
import CircleKeyIcon from 'zignal-landscape/lib/Icon/CircleKeyIcon';
import FFBInfoBanner from '../FFBInfoBanner';
import MfeGuruIcon from 'zignal-landscape/lib/Icon/MfeGuruIcon';
import AdditionalFilters from '../../components/AdditionalFilters';
import MfeIconWrapper from '../MfeIconWrapper/MfeIconWrapper';

import KeywordTranslation from '../KeywordTranslation';

// local storage key to track when to show Info Banner
const showInfoKey = 'showFFBEditorInfo';

const getShowInfoValue = () => {
  const value = JSON.parse(window.localStorage.getItem(showInfoKey));
  return value === null ? true : value;
};

const DEFAULT_ADDITIONAL_FILTERS_SETTINGS = {};
const DEFAULT_TRANSLATIONS = [];

const FFBEditor = ({
  styles,
  index,
  onModelChanged,
  fireSearch,
  value,
  error = null,
  colors,
  addSearchTerm,
  placeholder,
  additionalFiltersSettings = DEFAULT_ADDITIONAL_FILTERS_SETTINGS,
  updateAdditionalFilters,
  closeAdditonalFilters,
  openLanguageSelector,
  removeLanguage,
  translateKeywords,
  toggleKeyword,
  translations = DEFAULT_TRANSLATIONS,
  enabledKeywordsCount,
  isFetching,
  areTranslationsObsolete,
  makeTranslationsObsolete
}) => {
  const [ editor, setEditor ] = useState();
  const [ showInfo, setShowInfo ] = useState(false);
  const [ showPlaceholder, setShowPlaceholder ] = useState(true);
  const isInternalChange = useRef(false);

  useEffect(() => {
    if (!editor) {
      monaco.languages.register({ id: 'zql' });
      const editorInstance = monaco.editor.create(document.getElementById(`discover-ffb-editor-${index}`), {
        acceptSuggestionOnEnter: 'off',
        language: 'zql',
        formatOnType: true,
        fontSize: '18px',
        minimap: {
          enabled: false
        },
        parameterHints:{
          enabled: false
        },
        scrollbar:{
          useShadows: false,
          vertical: 'hidden'
        },
        lineNumbers: 'off',
        lineNumbersMinChars: 1,
        quickSuggestions: false,
        renderIndentGuides: false,
        tabCompletion: 'off',
        wordBasedSuggestions: false,
        wordWrap: 'off',
        contextmenu: false,
        overviewRulerBorder: false,
        overviewRulerLanes: 0,
        renderLineHighlight: 'none',
        lineDecorationsWidth: 0,
        hideCursorInOverviewRuler: true,
        occurrencesHighlight: false,
        selectionHighlight: false,
        automaticLayout: true,
      });

      monaco.editor.defineTheme('zignal', {
        base: 'vs-dark',
        inherit: true,
        rules: [
          { token: 'delimeter.parenthesis', foreground: 'ff0000' },
          { token: 'keyword', foreground: '5296EC' },
          { token: 'string', foreground: 'FFFFFF' },
          { token: '', foreground: 'FFFFFF' }
        ],
        colors: {
          'editor.background': '#1E2430',
          'editorHoverWidget.background' : '#161c26',
          'editorOverviewRuler.errorForeground' : '#FFFFFF',
          'editorOverviewRuler.bracketMatchForeground' : '#FFFFFF',
        }
      });

      editorInstance.getModel().onDidChangeContent(() => {
        isInternalChange.current = true;
        onModelChanged(editorInstance.getValue());
      });

      editorInstance.onMouseDown(() => {
        if (getShowInfoValue()) {
          setShowInfo(true);
        }
      });

      editorInstance.onKeyDown((e) => {
        makeTranslationsObsolete();
        if(e.code === 'Enter' || e.code === 'NumpadEnter') {
          fireSearch(editorInstance.getValue());
          setShowInfo(false);
          e.preventDefault();
        } else if (e.shiftKey && e.code === 'Tab') {
          document.activeElement.blur();
        } else if (e.code === 'Tab') {
          document.activeElement.blur();
          const nextContainer = document.getElementById(`discover-ffb-editor-${index + 1}`);
          if (isEmpty(nextContainer) && !isEmpty(editorInstance.getValue())) {
            addSearchTerm();
            e.preventDefault();
          }
        }
      });

      /* the check below is to resolve an issue with testing the the monaco editor
      and the two event handlers below. */
      if (typeof editorInstance.onDidFocusEditorText === 'function') {
        editorInstance.onDidFocusEditorText(() => {
          setShowPlaceholder(false);

          const showInfoVal = window.localStorage.getItem(showInfoKey);
          if (showInfoVal === null || (/true/i).test(showInfoVal)) {
            setShowInfo(true);
          }
        });

        editorInstance.onDidBlurEditorText(() => {
          if (isEmpty(editorInstance.getValue())) {
            setShowPlaceholder(true);
          }
        });
      }

      monaco.editor.setTheme('zignal');
      monaco.languages.registerCompletionItemProvider('zql', monaco.freeFormBuilderCompletionItemProvider);
      monaco.languages.setMonarchTokensProvider('zql', monaco.freeFormBuilderConfiguration);
      monaco.languages.setLanguageConfiguration('zql', monaco.freeFormBuilderConfigurationBrackets);

      setEditor(editorInstance);
    }
  }, []);

  useEffect(() => {
    if (editor && value !== '') {
      if (isInternalChange.current) {
        isInternalChange.current = false;
      } else {
        editor.setValue(value);
      }
      setShowPlaceholder(false);
    }
  }, [value, editor]);

  const onClickOutsideFFBInfoBanner = () => {
    /**
     * The requirement is to close the FFBInfoBanner element,
     * -- if the MfeGuruIcon is clicked or
     * -- if anywhere outside the FFBInfoBanner element is clicked.
     *
     * MfeGuruIcon is outside the FFBInfoBanner element. the
     * setImmediate() here allows other event handlers, including onShowInfo()
     * to happen first. That in turn calls setShowInfo(false) because
     * showInfo is true when the FFBInfoBanner element is displayed.
     */
    setImmediate(() => {
      if (editor) {
        editor.focus();
      }
      window.localStorage.setItem(showInfoKey, false);
      setShowInfo(false); // needed when MfeGuruIcon is not clicked
    });
  };

  const onShowInfo = () => {
    if (editor) {
      editor.focus();
    }
    setShowInfo(!showInfo);
  };

  const onCircleKeyIconClick = () => {
    const editorValue = editor ? editor.getValue() : '';
    if (!editorValue) {
      return;
    }
    setShowInfo(false);
  };

  const onPlaceholderClick = () => {
    setShowPlaceholder(false);
    if (editor) {
      editor.focus();
    }
  };

  return (
    // Not sure why, but this would not work with a class, it needs to be styled inline.
    // the offset in the calc is tied to .rightSection width
    <div style={{ display: 'inline-block', width: 'calc(100% - 202px)', position: 'relative' }}>
      <GridContent>
        <div className={styles.FFBEditorContainer} style={{borderColor: error ? '#DD3A59' : null}}>
          <div className={styles.FFBEditor} id={`discover-ffb-editor-${index}`} style={{display:'flex'}}>
            <div className={styles.monacoMultiSelectContainerIcon} style={{lineHeight: '40px', paddingLeft: '10px'}}>
              <CircleKeyIcon
                width={22}
                height={22}
                style={{ fill: colors[index]}}
                onClick={onCircleKeyIconClick}
              />
            </div>
            {placeholder && showPlaceholder ? (
              <div className={styles.FFBEditorPlaceholder} onClick={onPlaceholderClick}>
                <span className={styles.FFBEditorPlaceholder__text}>{placeholder}</span>
              </div>
            ) : null}
          </div>
          <div className={styles.FFBEditorInfo}>
            <MfeIconWrapper icon={MfeGuruIcon} onClick={onShowInfo} open={!!showInfo} showChevron={false} iconProps={{ style: { fill: 'currentColor', height: 24, width:24 } }} />
          </div>
        </div>
        {showInfo && (<FFBInfoBanner onClick={onClickOutsideFFBInfoBanner} />)}
        {error && (
          <div
            className="ffbErrorMessage"
            style={{paddingTop: '5px', color: '#DD3A59', fontStyle: 'italic'}}
          >
            {error}
          </div>
        )}
        { additionalFiltersSettings.open ?
          <AdditionalFilters
            index={index}
            settings={additionalFiltersSettings}
            updateAdditionalFilters={updateAdditionalFilters}
            closeAdditonalFilters={closeAdditonalFilters}
          /> : null
        }
        <KeywordTranslation
          translations={translations}
          enabledKeywordsCount={enabledKeywordsCount}
          areTranslationsObsolete={!isEmpty(translations) && areTranslationsObsolete}
          makeTranslationsObsolete={makeTranslationsObsolete}
          handleAddLanguage={() => openLanguageSelector(index)}
          handleRemoveLanguage={code => removeLanguage(code)}
          handleToggleKeyword={toggleKeyword}
          handleUpdateTranslations={() => translateKeywords()}
          isFetching={isFetching}
        />
      </GridContent>
    </div>
  );
};

FFBEditor.propTypes = {
  styles: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  onModelChanged: PropTypes.func.isRequired,
  fireSearch: PropTypes.func.isRequired,
  removeLanguage: PropTypes.func.isRequired,
  translateKeywords: PropTypes.func.isRequired,
  toggleKeyword: PropTypes.func.isRequired,
  value: PropTypes.string.isRequired,
  error: PropTypes.any,
  colors: PropTypes.array.isRequired,
  addSearchTerm: PropTypes.func,
  placeholder: PropTypes.string,
  additionalFiltersSettings: PropTypes.object,
  updateAdditionalFilters: PropTypes.func,
  closeAdditonalFilters: PropTypes.func,
  translations: PropTypes.array.isRequired,
  enabledKeywordsCount: PropTypes.number.isRequired,
  isFetching: PropTypes.bool.isRequired,
  makeTranslationsObsolete: PropTypes.func.isRequired
};

export default themeable(FFBEditor);
